<template>
  <div role="region" 
       aria-labelledby="slot-game-heading"
       @keydown.space.prevent="triggerSpin"
       @keydown.enter="triggerSpin"
       tabindex="0">
    
    <h3 id="slot-game-heading" class="sr-only">{{ $t('Accessibility.SlotMachine.Title') }}</h3>
    
    <!-- Game instructions for screen readers -->
    <div class="sr-only" aria-live="polite" id="slot-instructions">
      {{ gameInstructions }}
    </div>

    <!-- Current game state announcement -->
    <div class="sr-only" aria-live="polite" id="slot-status">
      {{ gameStatus }}
    </div>

    <canvas :style="canvasStyle()" 
            :class="completed ? 'completed' : ''" 
            ref="slot" 
            id="slot" 
            role="img"
            :aria-label="canvasAriaLabel">
    </canvas>
    
    <div id="start" class="text-center m-3">
      <button :style="buttonStyle()"
              @click="onStart"
              @keydown.enter="onStart"
              @keydown.space.prevent="onStart"
              class="btn btn-block"
              v-if="spinButtonVisible"
              :aria-label="spinButtonAriaLabel"
              v-text="data.spinButtonText">
      </button>
    </div>

    <!-- Keyboard instructions -->
    <div class="sr-only" v-if="spinButtonVisible">
      {{ $t('Accessibility.SlotMachine.KeyboardInstructions') }}
    </div>

  </div>
</template>

<script>

import SlotMachine from './subcomponents/slotmachine';

export default {
  props: {
    data: {},
    isDemo: false,
    inFrame: false,
    fontFamily: null,
    fontSize: null,
    viewportDimensions: {
      type: Object,
      default: () => ({ isDesktop: false })
    }
  },
  components: {
    //  Wheel,
  },
  data() {
    return {
      completed: false,
      slot: null,
      spinButtonVisible: true,
      spinning: false,
      gameStatus: ''
    };
  },
  computed: {
    gameInstructions() {
      return this.$t('Accessibility.SlotMachine.Instructions');
    },
    canvasAriaLabel() {
      if (this.completed) {
        return this.$t('Accessibility.SlotMachine.Completed');
      } else if (this.spinning) {
        return this.$t('Accessibility.SlotMachine.Spinning');
      } else {
        return this.$t('Accessibility.SlotMachine.Ready');
      }
    },
    spinButtonAriaLabel() {
      return this.$t('Accessibility.SlotMachine.SpinButton');
    }
  },
  mounted() {
    // Only apply margin on desktop
    const margin = this.viewportDimensions.isDesktop ? 15 : 0;
    const border = this.data.borderSize || 0;
    
    // For desktop, use the provided viewport width if available
    let viewportWidth;
    if (this.viewportDimensions.isDesktop && this.viewportDimensions.width) {
      viewportWidth = this.viewportDimensions.width;
    } else if (this.viewportDimensions.isDesktop) {
      viewportWidth = window.innerWidth;
    } else {
      // For mobile, account for potential scrollbar and padding
      viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
    }
    
    // Add extra padding on very small mobile screens to prevent edge touching
    const mobilePadding = (!this.viewportDimensions.isDesktop && viewportWidth < 420) ? 10 : 0;
    
    let innerWidthWithMargin = viewportWidth - (2 * margin) - (2 * border) - (2 * mobilePadding);

    // Use base width that respects desktop constraints
    const baseWidth = 440;
    const baseHeight = 240;
    
    // calculate aspect ratio
    let aspect = calculateAspectRatioFit(baseWidth, baseHeight, innerWidthWithMargin, window.innerHeight);
    const canvas = document.getElementById('slot');
    canvas.width = innerWidthWithMargin;
    canvas.height = aspect.height;
    this.slot = new SlotMachine(this.$refs.slot, this.$refs.spin, this.data, aspect, this.onComplete);

  },
  methods: {
    onComplete: function (event) {
      this.completed = true;
      this.spinning = false;
      this.gameStatus = this.$t('Accessibility.SlotMachine.Completed');
      
      // Announce completion to screen readers
      this.announceGameState(this.gameStatus);
      
      // `this` inside methods points to the Vue instance
      this.$emit('end', event);
    },
    onStart: function (event) {
      this.spinButtonVisible = false;
      this.spinning = true;
      this.gameStatus = this.$t('Accessibility.SlotMachine.Spinning');
      
      // Announce start to screen readers
      this.announceGameState(this.gameStatus);
      
      this.$emit('start', event);
      this.slot.spin();
    },
    
    triggerSpin() {
      // Keyboard accessibility - trigger spin via keyboard
      if (!this.spinning && !this.completed && this.spinButtonVisible) {
        this.onStart();
      }
    },
    
    announceGameState(message) {
      // Use the global screen reader announcement function if available
      if (this.$root.announceToScreenReader) {
        this.$root.announceToScreenReader(message);
      }
    },
    canvasStyle() {
      let backgroundColor = this.data.slotBackgroundColor;
      if (!backgroundColor) backgroundColor = "#000";
      let style = {
        backgroundColor: backgroundColor
      }
      
      // Apply margin-left on desktop, center on mobile
      if (this.viewportDimensions.isDesktop) {
        style['margin-left'] = '15px';
      } else {
        // Center the canvas on mobile
        style['margin'] = '0 auto';
        style['display'] = 'block';
      }
      
      let borderWidth = this.data.borderSize;
      let borderColor = this.data.borderColor;
      if (!borderColor) borderColor = "#000";

      if (borderWidth > 0) {
        style.border = `${borderWidth}px solid ${borderColor}`
      }

      return style;
    },
    buttonStyle() {

      let buttonTextColor = this.data.spinButtonTextColor;
      if (!buttonTextColor) buttonTextColor = "#fff";
      let buttonBackgroundColor = this.data.spinButtonBackgroundColor;
      if (!buttonBackgroundColor) buttonBackgroundColor = "#e04f1a";
      let buttonFontFamily = this.data.spinButtonFontFamily;
      if(!buttonFontFamily) buttonFontFamily = this.fontFamily;
      let buttonFontSize = this.data.spinButtonFontSize;
      if(!buttonFontSize) buttonFontSize = this.fontSize;

      let style = {
        color: buttonTextColor,
        backgroundColor: buttonBackgroundColor,
        borderColor: buttonBackgroundColor,
        fontFamily: buttonFontFamily,
        fontSize: buttonFontSize + 'px'
      };

      style.height = '60px';
      style.borderRadius = '8px';

      return style;
    },
  }
};

function calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
  let ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return {width: srcWidth * ratio, height: srcHeight * ratio, ratio: ratio};
}

</script>

<style scoped>

/* Screen reader only content */
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

#slot {
  box-sizing: content-box;
  display: block;
}

.completed {
  opacity: 0.9;
}

/* Ensure button doesn't cause overflow on mobile */
@media (max-width: 420px) {
  #start {
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 15px;
    padding-right: 15px;
  }
  
  #start .btn {
    max-width: 100%;
  }
}

.blur-completed {
  -webkit-filter: blur(1px); /* Safari */
  filter: blur(1px);
  -ms-filter: blur(1px);
  filter: progid:DXImageTransform.Microsoft.Blur(pixelradius='1', shadowopacity='0.0');
}

</style>
